import { format } from "date-fns";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { PersonalApplicantInformation } from "Store/interfaces/PersonalApplicantInformation";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { AkaCriticalIllnessApplicantInsuranceSpecification } from "Store/interfaces/products/checkout/lifeProducts/AkaCriticalIllnessProduct";
import { InsuranceCoverage } from "Store/interfaces/products/coverage";
import { isDefined } from "Utils/functions";
import { getAkaCriticalIllnessCoverageGroup } from "./getters";
import {
  isAkaCriticalIllnessCoInsuredSelected,
  isAkaCriticalIllnessInsuredSelected,
  MappingData,
} from "./mapToCartProducts";

export type AkaCriticalIllnessProductBackend = {
  name: INSURANCE_NAME_VALUE;
  coverageGroup: InsuranceCoverage;
  memberApplicantPersonInformation?: PersonalApplicantInformation;
  coInsuredApplicantPersonInformation?: PersonalApplicantInformation;
  memberApplicantInsuranceSpecification?: AkaCriticalIllnessApplicantInsuranceSpecification;
  coInsuredApplicantInsuranceSpecification?: AkaCriticalIllnessApplicantInsuranceSpecification;
};

export const mapToAkaCriticalIllnessProduct = (
  data: MappingData,
): AkaCriticalIllnessProductBackend => {
  return {
    name: INSURANCE_NAME_VALUE.AKA_CRITICAL_ILLNESS,
    coverageGroup: getAkaCriticalIllnessCoverageGroup(data.priceFormData),
    ...mapInsured(data),
    ...mapCoInsured(data),
  };
};

const mapInsured = (
  data: MappingData,
):
  | Pick<
      AkaCriticalIllnessProductBackend,
      | "memberApplicantPersonInformation"
      | "memberApplicantInsuranceSpecification"
    >
  | undefined => {
  if (!isAkaCriticalIllnessInsuredSelected(data.priceFormData)) {
    return undefined;
  }

  const { dateOfBirth } = data.customerData.newCustomerResponse;
  if (!isDefined(dateOfBirth)) {
    throw Error(
      "mapToAkaCriticalIllnessProduct - cannot map co insured cart product - date of birth is not defined",
    );
  }
  const { akaCriticalIllnessCoverageAmountGLevel } = data.priceFormData;
  if (!isDefined(akaCriticalIllnessCoverageAmountGLevel)) {
    throw Error(
      "mapToAkaCriticalIllnessProduct - cannot map to cart product - coverage amount g level is not defined",
    );
  }

  return {
    memberApplicantPersonInformation: {
      dateOfBirth,
    },
    memberApplicantInsuranceSpecification: {
      coverageAmountGLevel: Number(akaCriticalIllnessCoverageAmountGLevel),
    },
  };
};

const mapCoInsured = (
  data: MappingData,
):
  | Pick<
      AkaCriticalIllnessProductBackend,
      | "coInsuredApplicantPersonInformation"
      | "coInsuredApplicantInsuranceSpecification"
    >
  | undefined => {
  const { addCoInsured } = data.detailsFormData;
  if (
    addCoInsured !== BooleanFormType.TRUE ||
    !isAkaCriticalIllnessCoInsuredSelected(data.priceFormData)
  ) {
    return undefined;
  }

  const { coInsuredBirthDate } = data.detailsFormData;
  if (!isDefined(coInsuredBirthDate)) {
    throw Error(
      "mapToAkaCriticalIllnessProduct - cannot map co insured cart product - date of birth is not defined",
    );
  }
  const { akaCriticalIllnessCoverageAmountCoInsuredGLevel } =
    data.priceFormData;
  if (!isDefined(akaCriticalIllnessCoverageAmountCoInsuredGLevel)) {
    throw Error(
      "mapToAkaCriticalIllnessProduct - cannot map coinsured to cart product - coverage amount g level is not defined",
    );
  }

  return {
    coInsuredApplicantPersonInformation: {
      dateOfBirth: format(coInsuredBirthDate, "yyyy-MM-dd"),
    },
    coInsuredApplicantInsuranceSpecification: {
      coverageAmountGLevel: Number(
        akaCriticalIllnessCoverageAmountCoInsuredGLevel,
      ),
    },
  };
};
